import React from 'react';
import { Zap, Sparkles, UserCheck, Calendar, ArrowUpRight, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface UsageMeterProps {
  usedConversations?: number;
  totalLimit?: number;
  transferredConversations?: number;
  renewalDate?: string | null;
  nextQuotaResetDate?: string | null;
  NextQuotaResetDate?: string | null;
  planName?: string;
  isSubscribed?: boolean;
  planId?: string;
  interval?: string | null;
  onUpgradeClick?: () => void;
  isPaymentFailed?: boolean;
}

export const UsageMeter: React.FC<UsageMeterProps> = ({
  usedConversations = 0,
  totalLimit = 5000,
  transferredConversations = 0,
  renewalDate,
  nextQuotaResetDate,
  NextQuotaResetDate,
  planName = 'Pro',
  isSubscribed = true,
  planId,
  interval = 'monthly',
  onUpgradeClick,
  isPaymentFailed = false,
}) => {
  const percentage = totalLimit > 0 ? Math.min(100, Math.round((usedConversations / totalLimit) * 100)) : 0;
  const remaining = Math.max(0, totalLimit - usedConversations);
  const isExceeded = usedConversations >= totalLimit;
  const isNearLimit = !isExceeded && percentage >= 80;
  const isFreeUser = !isSubscribed || planId === 'free';
  const isFailedPayment = Boolean(isPaymentFailed);

  const humanAssistedCount = transferredConversations;
  const autonomousAICount = Math.max(0, usedConversations - humanAssistedCount);
  const autonomousPercent = usedConversations > 0 ? Math.round((autonomousAICount / usedConversations) * 100) : 0;
  const humanPercent = usedConversations > 0 ? Math.round((humanAssistedCount / usedConversations) * 100) : 0;

  const isPaidSubscriber = Boolean(isSubscribed && planId !== 'free' && renewalDate);

  const getCycleInfo = () => {
    const rawResetDate = nextQuotaResetDate || NextQuotaResetDate;
    let explicitResetFormatted: string | null = null;
    if (rawResetDate) {
      try {
        const parsed = new Date(rawResetDate);
        if (!isNaN(parsed.getTime())) {
          const isIso = typeof rawResetDate === 'string' && (rawResetDate.endsWith('Z') || rawResetDate.includes('T'));
          explicitResetFormatted = new Intl.DateTimeFormat('pt-BR', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            ...(isIso ? { timeZone: 'UTC' } : {}),
          }).format(parsed);
        }
      } catch {
        explicitResetFormatted = null;
      }
    }

    if (isPaidSubscriber && (renewalDate || rawResetDate)) {
      try {
        const end = new Date(renewalDate || rawResetDate!);
        if (!isNaN(end.getTime())) {
          const start = new Date(end);
          if (interval === 'annual') {
            start.setFullYear(start.getFullYear() - 1);
          } else {
            start.setMonth(start.getMonth() - 1);
          }
          const isIso = typeof (renewalDate || rawResetDate) === 'string' && ((renewalDate || rawResetDate)!.endsWith('Z') || (renewalDate || rawResetDate)!.includes('T'));
          const startFmt = new Intl.DateTimeFormat('pt-BR', { month: 'short', day: 'numeric', ...(isIso ? { timeZone: 'UTC' } : {}) });
          const endFmt = new Intl.DateTimeFormat('pt-BR', { month: 'short', day: 'numeric', ...(isIso ? { timeZone: 'UTC' } : {}) });
          const fullEndFmt = new Intl.DateTimeFormat('pt-BR', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            ...(isIso ? { timeZone: 'UTC' } : {}),
          });
          const resolvedResetDate = explicitResetFormatted || fullEndFmt.format(end);
          return {
            isPaid: true,
            windowText: `${startFmt.format(start)} ➔ ${endFmt.format(end)}`,
            resetDate: resolvedResetDate,
            hasExplicitResetDate: Boolean(explicitResetFormatted),
            resetBadge: explicitResetFormatted ? 'Data de Redefinição' : 'Dia de Renovação',
          };
        }
      } catch {
        // fallback to calendar
      }
    }

    // Free tier: defaults strictly to calendar month
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const nextMonthFirst = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const dayFmt = new Intl.DateTimeFormat('pt-BR', { month: 'short', day: 'numeric' });
    const resetFmt = new Intl.DateTimeFormat('pt-BR', { month: 'short', day: 'numeric', year: 'numeric' });

    return {
      isPaid: false,
      windowText: `${dayFmt.format(startOfMonth)} ➔ ${dayFmt.format(endOfMonth)}`,
      resetDate: explicitResetFormatted || '1º do mês',
      nextResetDateConcrete: explicitResetFormatted || resetFmt.format(nextMonthFirst),
      hasExplicitResetDate: Boolean(explicitResetFormatted),
      resetBadge: explicitResetFormatted ? 'Data de Redefinição' : '1º do Mês',
    };
  };

  const cycleInfo = getCycleInfo();

  return (
    <Card className="border-border/70 shadow-sm overflow-hidden bg-card">
      <CardHeader className="pb-3 border-b border-border/40 bg-muted/10">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div className="space-y-0.5">
            <CardTitle className="text-base flex items-center gap-2">
              <Zap className={`h-4 w-4 ${isExceeded && isFailedPayment ? 'text-destructive fill-destructive' : 'text-amber-500 fill-amber-500'}`} />
              Consumo Mensal de Conversas
            </CardTitle>
            <CardDescription className="text-xs">
              {cycleInfo.isPaid || cycleInfo.hasExplicitResetDate
                ? `Conversas no seu período atual (${cycleInfo.windowText}). O limite renova em ${cycleInfo.resetDate}.`
                : `Conversas neste mês (${cycleInfo.windowText}). O limite renova no dia 1º.`}
            </CardDescription>
          </div>

          <div className="flex items-center gap-2">
            <Badge
              className={
                isExceeded && isFailedPayment
                  ? 'bg-destructive/15 text-destructive border-destructive/30 font-semibold text-xs'
                  : 'bg-primary/10 text-primary border-primary/20 font-semibold text-xs'
              }
            >
              {planName}
            </Badge>
            {isExceeded && onUpgradeClick && (
              isFailedPayment ? (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={onUpgradeClick}
                  className="h-7 text-xs gap-1 font-medium"
                >
                  <span>Atualizar Pagamento</span>
                  <ArrowUpRight className="h-3 w-3" />
                </Button>
              ) : isFreeUser ? (
                <Button
                  size="sm"
                  onClick={onUpgradeClick}
                  className="h-7 text-xs gap-1 font-medium bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm"
                >
                  <Sparkles className="h-3 w-3" />
                  <span>Fazer Upgrade</span>
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onUpgradeClick}
                  className="h-7 text-xs gap-1 font-medium"
                >
                  <span>Mudar de Plano</span>
                  <ArrowUpRight className="h-3 w-3" />
                </Button>
              )
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-5 space-y-4">
        {/* Past Due / Over Quota Alert Banner */}
        {isExceeded && (
          isFailedPayment ? (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/25 text-destructive text-xs">
              <div className="flex items-center gap-2 font-medium">
                <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
                <span>
                  Houve uma falha no pagamento da assinatura. Atualize a forma de pagamento para reativar seu Agente de IA.
                </span>
              </div>
              {onUpgradeClick && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={onUpgradeClick}
                  className="h-7 text-xs font-semibold shrink-0"
                >
                  Atualizar Forma de Pagamento
                </Button>
              )}
            </div>
          ) : isFreeUser ? (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3.5 rounded-lg bg-primary/10 border border-primary/20 text-foreground text-xs">
              <div className="flex items-center gap-2 font-medium">
                <Sparkles className="h-4 w-4 shrink-0 text-primary" />
                <span>
                  Você atingiu o limite mensal de conversas. Faça upgrade do seu plano para manter seu Agente de IA atendendo os clientes.
                </span>
              </div>
              {onUpgradeClick && (
                <Button
                  size="sm"
                  onClick={onUpgradeClick}
                  className="h-7 text-xs font-semibold shrink-0 bg-primary text-primary-foreground hover:bg-primary/90 gap-1"
                >
                  <span>Ver Planos e Fazer Upgrade</span>
                  <ArrowUpRight className="h-3 w-3" />
                </Button>
              )}
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/25 text-foreground text-xs">
              <div className="flex items-center gap-2 font-medium">
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                <span>
                  A cota mensal de conversas foi atingida para este ciclo. Faça upgrade do seu plano para aumentar os limites.
                </span>
              </div>
              {onUpgradeClick && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onUpgradeClick}
                  className="h-7 text-xs font-semibold shrink-0 border-amber-500/40 hover:bg-amber-500/10 gap-1 text-foreground"
                >
                  <span>Mudar de Plano</span>
                  <ArrowUpRight className="h-3 w-3" />
                </Button>
              )}
            </div>
          )
        )}

        {/* Progress Numbers & Warning Badge */}
        <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-2 text-xs">
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-foreground tracking-tight">
              {usedConversations.toLocaleString()}
            </span>
            <span className="text-muted-foreground font-medium">
              / {totalLimit.toLocaleString()} conversas usadas
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            {isExceeded && (
              isFailedPayment ? (
                <Badge
                  variant="destructive"
                  className="bg-destructive text-destructive-foreground hover:bg-destructive text-xs font-semibold gap-1 py-0.5 px-2 shadow-sm"
                >
                  <AlertTriangle className="h-3 w-3" />
                  <span>Pagamento pendente — Atualize a forma de pagamento para reativar o Agente de IA</span>
                </Badge>
              ) : isFreeUser ? (
                <Badge
                  className="bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30 text-xs font-semibold gap-1 py-0.5 px-2 shadow-sm"
                >
                  <AlertTriangle className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                  <span>Limite atingido — Faça upgrade para reativar o Agente de IA</span>
                </Badge>
              ) : (
                <Badge
                  variant="outline"
                  className="border-amber-500/40 text-amber-700 dark:text-amber-400 bg-amber-500/10 text-xs font-semibold gap-1 py-0.5 px-2"
                >
                  <AlertTriangle className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                  <span>Limite atingido — Mude de plano para aumentar as conversas</span>
                </Badge>
              )
            )}
            {transferredConversations !== undefined && transferredConversations > 0 && (
              <Badge variant="outline" className="text-xs font-medium gap-1 text-muted-foreground border-border/60 bg-muted/30">
                <UserCheck className="h-3.5 w-3.5 text-emerald-500" />
                <span><strong className="text-foreground">{transferredConversations.toLocaleString()}</strong> atendidas pela equipe</span>
              </Badge>
            )}
            <div className="flex items-center gap-1.5">
              <span
                className={`font-bold text-sm ${isExceeded
                    ? isFailedPayment
                      ? 'text-destructive'
                      : 'text-amber-600 dark:text-amber-400'
                    : isNearLimit
                      ? 'text-amber-500'
                      : 'text-foreground'
                  }`}
              >
                {percentage}%
              </span>
              <span className="text-muted-foreground">({remaining.toLocaleString()} restantes)</span>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="h-3 w-full bg-secondary rounded-full overflow-hidden p-0.5 border border-border/40">
          <div
            className={`h-full rounded-full transition-all duration-500 ease-out ${isExceeded
                ? isFailedPayment
                  ? 'bg-destructive'
                  : 'bg-amber-500'
                : isNearLimit
                  ? 'bg-amber-500'
                  : 'bg-primary'
              }`}
            style={{ width: `${percentage}%` }}
          />
        </div>

        {/* Small note under the progress bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 text-[11px] text-muted-foreground pt-0.5">
          <span className="flex items-center gap-1.5">
            <Calendar className="h-3 w-3 text-muted-foreground/70" />
            <span>
              {cycleInfo.isPaid || cycleInfo.hasExplicitResetDate
                ? `O limite renova em ${cycleInfo.resetDate} (Período: ${cycleInfo.windowText})`
                : `O limite renova no 1º dia do mês (Período: ${cycleInfo.windowText})`}
            </span>
          </span>
          {isExceeded && (
            isFailedPayment ? (
              <span className="font-semibold text-destructive flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                Agente de IA pausado (pagamento pendente)
              </span>
            ) : isFreeUser ? (
              <span className="font-medium text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                Limite atingido — Agente de IA pausado até a renovação ou upgrade
              </span>
            ) : (
              <span className="font-medium text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                Agente de IA pausado (limite atingido)
              </span>
            )
          )}
        </div>

        {/* Usage Breakdown Sub-metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
          <div className="p-3 rounded-lg bg-muted/30 border border-border/40 space-y-1">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Sparkles className="h-3.5 w-3.5 text-violet-500" />
              <span>Resolvidas pelo Agente de IA</span>
            </div>
            <p className="text-sm font-bold text-foreground">
              {autonomousAICount.toLocaleString()}{' '}
              <span className="text-[10px] text-muted-foreground font-normal">({autonomousPercent}%)</span>
            </p>
          </div>

          <div className="p-3 rounded-lg bg-muted/30 border border-border/40 space-y-1">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <UserCheck className="h-3.5 w-3.5 text-emerald-500" />
              <span>Atendidas pela Equipe</span>
            </div>
            <p className="text-sm font-bold text-foreground">
              {humanAssistedCount.toLocaleString()}{' '}
              <span className="text-[10px] text-muted-foreground font-normal">({humanPercent}%)</span>
            </p>
          </div>

          <div className="p-3 rounded-lg bg-muted/30 border border-border/40 space-y-1">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Calendar className="h-3.5 w-3.5 text-blue-500" />
              <span>Próxima Renovação</span>
            </div>
            <p className="text-sm font-bold text-foreground">
              {cycleInfo.isPaid ? cycleInfo.resetDate : cycleInfo.nextResetDateConcrete}{' '}
              <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">
                ({cycleInfo.resetBadge})
              </span>
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
